import React from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import { FieldTextInput, FieldSelect, FieldCheckbox, FieldRadioButton, FieldPhoneNumberInput } from '../../../components';
import { Checkbox, Radio, FormControlLabel, FormGroup, RadioGroup, FormControl, FormLabel, FormHelperText, Select, MenuItem, TextField } from '@mui/material';
import css from './ApplicationForm.module.css';
import MultiDropdownField from './MultiDropdownField';
import SlidingScaleGroupField from './SlidingScaleGroupField';
import ProfilePhotoUpload from './ProfilePhotoUpload';
import ImageUpload from './ImageUpload';
import RankingField from './RankingField';
import FileDropzoneField from './FileDropZone';
import { InfoOutlined } from '@mui/icons-material';

const QuestionField = ({ field, intl, formId, idx, input, readOnly, maxWords }) => {
  const { id, type, description, label, placeholder, required, options, info } = field;
  // Helper to get unique name
  const name = input.name;
  const getName = () => name;
  const getId = () => (formId ? `${formId}.${name}` : name);
  const renderField = () => {
    switch (type) {
      case 'multiDropdown':
        return (
          <Field
            name={getName()}
            render={props => (
              <MultiDropdownField
                {...props}
                label={intl.formatMessage({ id: label })}
                dropdowns={field.dropdowns}
                readOnly={readOnly}
              />
            )}
          />
        );
      case 'textarea':
        return (
          <Field
            name={getName()}
            render={({ input, meta }) => (
              <div>
                <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                  {intl.formatMessage({ id: label })}
                  {required && <span className={css.required}> *</span>}
                </FormLabel>
                
                <TextField
                  {...input}
                  id={getId()}
                  placeholder={placeholder ? intl.formatMessage({ id: placeholder }) : ''}
                  multiline
                  rows={1}
                  fullWidth
                  variant="outlined"
                  size="medium"
                  error={meta.touched && meta.error}
                  helperText={meta.touched && meta.error ? meta.error : ''}
                  className={css.textareaField}
                  InputProps={{
                    readOnly: readOnly,
                    inputProps: {
                      maxLength: maxWords
                    }
                  }}
                />
                {maxWords && (
                  <div style={{ width: '100%', justifyContent: 'flex-end', display: 'flex', alignItems: 'top', color: '#9e9e9e', fontSize: 12, marginTop: 8 }}>
                    <InfoOutlined style={{ fontSize: 16, marginRight: 4 }} />
                    <span style={{ lineHeight: 'normal' }}>
                      {intl.formatMessage({ id: 'ApplicationForm.maxWordsInfo' }, { maxWords })}
                    </span>
                  </div>
                )}
              </div>
            )}
            validate={required ? (value) => (!value ? intl.formatMessage({ id: 'ApplicationForm.fieldRequired' }) : undefined) : undefined}
          />
        );
      case 'select':
        return (
          <Field
            name={getName()}
            render={({ input, meta }) => (
              <div>
                <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                  {intl.formatMessage({ id: label })}
                  {required && <span className={css.required}> *</span>}
                </FormLabel>
                <FormControl
                  fullWidth
                  error={meta.touched && meta.error}
                  className={css.selectField}
                  disabled={readOnly}
                >
                  <Select
                    value={input.value || ''}
                    onChange={(event) => !readOnly && input.onChange(event.target.value)}
                    displayEmpty
                    variant="outlined"
                    size="medium"
                    readOnly={readOnly}
                  >
                    <MenuItem value="">
                      {intl.formatMessage({ id: 'ApplicationForm.selectPlaceholder' })}
                    </MenuItem>
                    {options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {isNaN(option.label) ? intl.formatMessage({ id: option.label }) : option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {meta.touched && meta.error && (
                    <FormHelperText>{meta.error}</FormHelperText>
                  )}
                </FormControl>
              </div>
            )}
          />
        );
      case 'month':
        const monthOptions = [
          { value: '1', label: 'January' },
          { value: '2', label: 'February' },
          { value: '3', label: 'March' },
          { value: '4', label: 'April' },
          { value: '5', label: 'May' },
          { value: '6', label: 'June' },
          { value: '7', label: 'July' },
          { value: '8', label: 'August' },
          { value: '9', label: 'September' },
          { value: '10', label: 'October' },
          { value: '11', label: 'November' },
          { value: '12', label: 'December' },
        ];
        return (
          <Field
            name={getName()}
            render={({ input, meta }) => (
              <div>
                <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                  {intl.formatMessage({ id: label })}
                  {required && <span className={css.required}> *</span>}
                </FormLabel>
                <FormControl
                  fullWidth
                  error={meta.touched && meta.error}
                  className={css.selectField}
                  disabled={readOnly}
                >
                  <Select
                    value={input.value || ''}
                    onChange={(event) => !readOnly && input.onChange(event.target.value)}
                    displayEmpty
                    variant="outlined"
                    size="medium"
                    readOnly={readOnly}
                  >
                    {monthOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {
                    info && (
                      <FormHelperText style={{ marginTop: 4 }}>{intl.formatMessage({ id: info })}</FormHelperText>
                    )
                  }
                  {meta.touched && meta.error && (
                    <FormHelperText>{meta.error}</FormHelperText>
                  )}
                </FormControl>
              </div>
            )}
          />
        );
      case 'year':
        const yearOptions = [];
        const currentYear = new Date().getFullYear();
        for (let y = currentYear; y >= 1980; y--) {
          yearOptions.push({ value: y, label: String(y) });
        }
        return (
          <Field
            name={getName()}
            render={({ input, meta }) => (
              <div>
                <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                  {intl.formatMessage({ id: label })}
                  {required && <span className={css.required}> *</span>}
                </FormLabel>
                <FormControl
                  fullWidth
                  error={meta.touched && meta.error}
                  className={css.selectField}
                  disabled={readOnly}
                >
                  <Select
                    value={input.value || ''}
                    onChange={(event) => !readOnly && input.onChange(event.target.value)}
                    displayEmpty
                    variant="outlined"
                    size="medium"
                    readOnly={readOnly}
                  >
                    {yearOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {meta.touched && meta.error && (
                    <FormHelperText>{meta.error}</FormHelperText>
                  )}
                </FormControl>
              </div>
            )}
          />
        );
      case 'checkboxGroup':
        return (
          <Field
            name={getName()}
            render={({ input, meta }) => (
              <div>
                <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                  {intl.formatMessage({ id: label })}
                  {required && <span className={css.required}> *</span>}
                </FormLabel>
                <FormControl
                  component="fieldset"
                  error={meta.touched && meta.error}
                  className={css.checkboxGroup}
                  disabled={readOnly}
                >
                  <FormGroup className={css.checkboxOptions}>
                    {options.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            checked={input.value?.includes(option.value) || false}
                            onChange={(event) => {
                              if (!readOnly) {
                                const newValue = event.target.checked
                                  ? [...(input.value || []), option.value]
                                  : (input.value || []).filter(val => val !== option.value);
                                input.onChange(newValue);
                              }
                            }}
                            className={css.checkboxInput}
                            disabled={readOnly}
                          />
                        }
                        label={
                          <span className={css.checkboxLabel}>
                            {id === 'maxClients' ? option.label : intl.formatMessage({ id: option.label })}
                          </span>
                        }
                        className={css.checkboxOption}
                      />
                    ))}
                  </FormGroup>
                  
                   {/* Render text input for "other" options when selected */}
                   {options.map((option) => {
                     if (option.type === 'text' && input.value?.includes(option.value)) {
                       return (
                         <Field
                           key={`${option.value}-text`}
                           name={`${getName()}_${option.value}Text`}
                           render={({ input: textInput, meta: textMeta }) => (
                             <div style={{ marginTop: 16, marginLeft: 0, marginRight: 0, width: '100%' }}>
                               <TextField
                                 {...textInput}
                                 placeholder={`Please specify ${intl.formatMessage({ id: option.label }).toLowerCase()}`}
                                 fullWidth
                                 variant="outlined"
                                 size="medium"
                                 error={textMeta.touched && textMeta.error}
                                 helperText={textMeta.touched && textMeta.error ? textMeta.error : ''}
                                 InputProps={{
                                   readOnly: readOnly,
                                 }}
                                 style={{ width: '100%' }}
                               />
                             </div>
                           )}
                         />
                       );
                     }
                     return null;
                   })}
                  
                  {meta.touched && meta.error && (
                    <FormHelperText className={css.fieldError}>{meta.error}</FormHelperText>
                  )}
                </FormControl>
              </div>
            )}
          />
        );
      case 'radioGroup':
        return (
          <Field
            name={getName()}
            render={({ input, meta }) => (
              <div>
                <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                  {intl.formatMessage({ id: label })}
                  {required && <span className={css.required}> *</span>}
                </FormLabel>
                <FormControl
                  component="fieldset"
                  error={meta.touched && meta.error}
                  className={css.radioGroup}
                  disabled={readOnly}
                >
                  <RadioGroup
                    value={input.value || ''}
                    onChange={(event) => !readOnly && input.onChange(event.target.value)}
                    className={css.radioOptions}
                  >
                    {options.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio className={css.radioInput} disabled={readOnly} />}
                        label={
                          <span className={css.radioLabel}>
                            {isNaN(option.label) ? (id === 'maxClients' ? option.label : intl.formatMessage({ id: option.label })) : option.label}
                          </span>
                        }
                        className={css.radioOption}
                        disabled={readOnly}
                      />
                    ))}
                  </RadioGroup>
                  {meta.touched && meta.error && (
                    <FormHelperText className={css.fieldError}>{meta.error}</FormHelperText>
                  )}
                </FormControl>
              </div>
            )}
          />
        );
      case 'imageSelect':
        // Only for careStyle, render 2x2 grid with 4 images
        const careStyleOptions = [
          { value: 'positive_reinforcement', label: 'Positive Reinforcement', img: '/static/images/carestyle_positive_reinforcement.jpg' },
          { value: 'positive_punishment', label: 'Positive Punishment', img: '/static/images/carestyle_positive_punishment.jpg' },
          { value: 'negative_reinforcement', label: 'Negative Reinforcement', img: '/static/images/carestyle_negative_reinforcement.jpg' },
          { value: 'negative_punishment', label: 'Negative Punishment', img: '/static/images/carestyle_negative_punishment.jpg' },
        ];
        
        // Check if this is a multi-select field (for providers) or single select (for customers)
        const isMultiSelect = field.multiSelect === true;
        
        return (
          <Field name={getName()}>
            {({ input, meta }) => (
              <div>
                <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                  {intl.formatMessage({ id: label })}
                  {required && <span className={css.required}> *</span>}
                </FormLabel>
                <div className={css.careStyleImageSelectWrapper}>
                  <div className={css.careStyleGrid}>
                    {careStyleOptions.map((opt, i) => {
                      const isSelected = isMultiSelect 
                        ? (input.value?.includes(opt.value) || false)
                        : (input.value === opt.value);
                      
                      return (
                        <div
                          key={opt.value}
                          className={
                            isSelected 
                              ? `${css.careStyleImageOption} ${css.careStyleImageSelected}`
                              : css.careStyleImageOption
                          }
                          onClick={() => {
                            if (readOnly) return;
                            
                            if (isMultiSelect) {
                              // Multi-select for providers
                              const newValue = isSelected
                                ? input.value.filter(v => v !== opt.value)
                                : [...(input.value || []), opt.value];
                              input.onChange(newValue);
                            } else {
                              // Single select for customers
                              console.log('Single select new value:', opt.value);
                              input.onChange(opt.value);
                            }
                          }}
                          tabIndex={readOnly ? -1 : 0}
                          role="button"
                          style={readOnly ? { cursor: 'not-allowed', opacity: 0.7 } : {}}
                          aria-pressed={isSelected}
                        >
                          <img src={opt.img} alt={opt.label} className={css.careStyleImg} />
                          <div className={css.careStyleImgLabel}>{opt.label}</div>
                        </div>
                      );
                    })}
                  </div>
                  {meta.touched && meta.error && (
                    <span className={css.fieldError}>{meta.error}</span>
                  )}
                </div>
              </div>
            )}
          </Field>
        );
      case 'frequency':
        return (
          <Field
            name={getName()}
            render={props => (
              <MultiDropdownField
                {...props}
                label={intl.formatMessage({ id: label })}
                dropdowns={[
                  {
                    name: 'number',
                    options: Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: `${String(i + 1)} times a ` })),
                  },
                  {
                    name: 'unit',
                    options: [
                      { value: 'day', label: 'day' },
                      { value: 'everyOtherDay', label: 'every other day' },
                      { value: 'week', label: 'week' },
                      { value: 'never', label: 'never' },
                    ],
                  },
                ]}
                readOnly={readOnly}
              />
            )}
          />
        );
      case 'pay': {
        // Use min/max/unit from field config
        const min = field.min || 1;
        const max = field.max || 100;
        const unit = field.unit || 'hour';

        // Define limits for each unit
        const unitLimits = {
          hour: { min: 1, max: 100 },
          day: { min: 1, max: 500 },
          week: { min: 1, max: 5000 }
        };

        const unitOptions = [
          { value: 'hour', label: 'ApplicationForm.perHour' },
          { value: 'day', label: 'ApplicationForm.perDay' },
          { value: 'week', label: 'ApplicationForm.perWeek' }
        ];

        return (
          <Field
            name={getName()}
            render={({ input, meta }) => {
              const currentValue = input.value || {};
              const selectedUnit = currentValue.unit || 'hour';
              const currentAmount = currentValue.amount || '';
              const limits = unitLimits[selectedUnit];

              return (
                <div>
                  <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                    {intl.formatMessage({ id: label })}
                    {required && <span className={css.required}> *</span>}
                  </FormLabel>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <TextField
                      type="number"
                      placeholder="Enter amount"
                      value={currentAmount}
                      onChange={(e) => {
                        if (!readOnly) {
                          const newAmount = e.target.value;
                          input.onChange({
                            ...currentValue,
                            amount: newAmount
                          });
                        }
                      }}
                      onBlur={() => {
                        if (!readOnly) {
                          input.onBlur();
                        }
                      }}
                      inputProps={{
                        min: limits.min,
                        max: limits.max,
                        style: { width: '100%' }
                      }}
                      variant="outlined"
                      size="small"
                      error={meta.touched && (meta.error || (currentAmount && (currentAmount < limits.min || currentAmount > limits.max)))}
                      style={{ minWidth: 150, width: 150 }}
                      disabled={readOnly}
                    />
                    <FormControl style={{ minWidth: 80 }} disabled={readOnly}>
                      <Select
                        value={selectedUnit}
                        onChange={(e) => {
                          if (!readOnly) {
                            const newUnit = e.target.value;
                            input.onChange({
                              ...currentValue,
                              unit: newUnit,
                              amount: currentAmount > unitLimits[newUnit].max ? '' : currentAmount
                            });
                          }
                        }}
                        variant="outlined"
                        size="small"
                      >
                        {unitOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {intl.formatMessage({ id: option.label })}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  {(meta.touched && meta.error) || (currentAmount && (currentAmount < limits.min || currentAmount > limits.max)) ? (
                    <div style={{
                      color: '#d32f2f',
                      fontSize: '0.75rem',
                      marginTop: '3px',
                      lineHeight: '1.2'
                    }}>
                      {meta.touched && meta.error ? meta.error :
                        currentAmount && (currentAmount < limits.min || currentAmount > limits.max) ?
                          `${limits.min}-${limits.max}` : ''}
                    </div>
                  ) : null}
                </div>
              );
            }}
            validate={required ? (value) => {
              if (!value || !value.amount) {
                return intl.formatMessage({ id: 'ApplicationForm.fieldRequired' });
              }
              const limits = unitLimits[value.unit || 'hour'];
              const amount = parseFloat(value.amount);
              if (isNaN(amount) || amount < limits.min || amount > limits.max) {
                return intl.formatMessage({
                  id: 'ApplicationForm.payAmountInvalid',
                  defaultMessage: `Amount must be between ${limits.min} and ${limits.max}`
                }, {
                  min: limits.min,
                  max: limits.max
                });
              }
              return undefined;
            } : (value) => {
              // Even if not required, validate the amount if it exists
              if (value && value.amount) {
                const limits = unitLimits[value.unit || 'hour'];
                const amount = parseFloat(value.amount);
                if (isNaN(amount) || amount < limits.min || amount > limits.max) {
                  return intl.formatMessage({
                    id: 'ApplicationForm.payAmountInvalid',
                    defaultMessage: `Amount must be between ${limits.min} and ${limits.max}`
                  }, {
                    min: limits.min,
                    max: limits.max
                  });
                }
              }
              return undefined;
            }}
          />
        );
      }
      case 'advanceBooking':
        return (
          <Field
            name={getName()}
            render={props => (
              <MultiDropdownField
                {...props}
                label={intl.formatMessage({ id: label })}
                dropdowns={[
                  {
                    name: 'number',
                    options: Array.from({ length: 30 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) })),
                  },
                  {
                    name: 'unit',
                    options: [
                      { value: 'day', label: 'day' },
                      { value: 'week', label: 'week' },
                      { value: 'month', label: 'month' },
                    ],
                  },
                ]}
                readOnly={readOnly}
              />
            )}
          />
        );
      case 'slidingScaleGroup':
         return (
           <SlidingScaleGroupField
             name={getName()}
             label={label}
             options={field.options}
             required={required}
             readOnly={readOnly}
             sliderLabels={field.sliderLabels}
             noneLabel={field.noneLabel}
             otherLabel={field.otherLabel}
           />
         );
       case 'profilePhotoUpload':
         return (
           <Field
             name={getName()}
             render={({ input, meta }) => (
               <div>
                 <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                   {intl.formatMessage({ id: label })}
                   {required && <span className={css.required}> *</span>}
                 </FormLabel>
                 <ProfilePhotoUpload
                   name={input.name}
                   value={input.value}
                   onChange={!readOnly ? input.onChange : () => {}}
                   uploadInProgress={false}
                   uploadError={null}
                 />
                 {meta.touched && meta.error && (
                   <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '3px' }}>
                     {meta.error}
                   </div>
                 )}
               </div>
             )}
             validate={required ? (value) => (!value ? intl.formatMessage({ id: 'ApplicationForm.fieldRequired' }) : undefined) : undefined}
           />
         );
       case 'petPhotoUpload':
         // Only apply this logic for ExtendedCustomerProfileForm
         if (formId === 'ExtendedCustomerProfileForm') {
           return (
             <Field
               name={getName()}
               render={({ input, meta }) => (
                 <div>
                   <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                     {intl.formatMessage({ id: label })}
                     {required && <span className={css.required}> *</span>}
                   </FormLabel>
                   <ImageUpload
                     value={input.value || []}
                     onChange={!readOnly ? input.onChange : () => {}}
                     maxFiles={field.maxFiles || 4}
                     readOnly={readOnly}
                   />
                   {meta.touched && meta.error && (
                     <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '3px' }}>
                       {meta.error}
                     </div>
                   )}
                 </div>
               )}
               validate={required ? (value) => (!value || value.length === 0 ? intl.formatMessage({ id: 'ApplicationForm.fieldRequired' }) : undefined) : undefined}
             />
           );
         }
         // Fall through to default case for other forms
       case 'phone':
         return (
           <Field
             name={getName()}
             render={({ input, meta }) => (
               <div>
                 <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                   {intl.formatMessage({ id: label })}
                   {required && <span className={css.required}> *</span>}
                 </FormLabel>
                 <FieldPhoneNumberInput
                   id={getId()}
                   name={input.name}
                   label=""
                   placeholder={placeholder ? intl.formatMessage({ id: placeholder }) : ''}
                   required={required}
                   readOnly={readOnly}
                   value={input.value}
                   onChange={input.onChange}
                   onBlur={input.onBlur}
                   onFocus={input.onFocus}
                   error={meta.touched && meta.error ? meta.error : undefined}
                 />
               </div>
             )}
             validate={required ? (value) => (!value ? intl.formatMessage({ id: 'ApplicationForm.fieldRequired' }) : undefined) : undefined}
           />
         );
       case 'email':
         return (
           <Field
             name={getName()}
             render={({ input, meta }) => (
               <div>
                 <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                   {intl.formatMessage({ id: label })}
                   {required && <span className={css.required}> *</span>}
                 </FormLabel>
                 <TextField
                   {...input}
                   id={getId()}
                   type="email"
                   placeholder={placeholder ? intl.formatMessage({ id: placeholder }) : ''}
                   fullWidth
                   variant="outlined"
                   size="medium"
                   error={meta.touched && meta.error}
                   helperText={meta.touched && meta.error ? meta.error : ''}
                   className={css.textField}
                   InputProps={{
                     readOnly: readOnly,
                   }}
                 />
               </div>
             )}
             validate={required ? (value) => (!value ? intl.formatMessage({ id: 'ApplicationForm.fieldRequired' }) : undefined) : undefined}
           />
         );
       case 'date':
         return (
           <Field
             name={getName()}
             render={({ input, meta }) => (
               <div>
                 <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                   {intl.formatMessage({ id: label })}
                   {required && <span className={css.required}> *</span>}
                 </FormLabel>
                 <TextField
                   {...input}
                   id={getId()}
                   type="date"
                   placeholder={placeholder ? intl.formatMessage({ id: placeholder }) : ''}
                   fullWidth
                   variant="outlined"
                   size="medium"
                   error={meta.touched && meta.error}
                   helperText={meta.touched && meta.error ? meta.error : ''}
                   className={css.textField}
                   InputProps={{
                     readOnly: readOnly,
                   }}
                   InputLabelProps={{
                     shrink: true,
                   }}
                 />
               </div>
             )}
             validate={required ? (value) => (!value ? intl.formatMessage({ id: 'ApplicationForm.fieldRequired' }) : undefined) : undefined}
           />
         );
       case 'rank':
        return (
          <Field
            name={getName()}
            render={(props) => (
              <RankingField
                {...props}
                label={label}
                description={description}
                options={options}
                readOnly={readOnly}
              />
            )}
          />
        );
      case 'applicationFilesUpload':
        return (
          <Field
            name={getName()}
            render={({ input, meta }) => (
              <div>
                <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                  {intl.formatMessage({ id: label })}
                  {required && <span className={css.required}> *</span>}
                </FormLabel>
                <FileDropzoneField
                  name={input.name}
                  input={input}
                  meta={meta}
                />
                {meta.touched && meta.error && (
                  <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '3px' }}>
                    {meta.error}
                  </div>
                )}
              </div>
            )}
            validate={required ? (value) => (!value || value.length === 0 ? intl.formatMessage({ id: 'ApplicationForm.fieldRequired' }) : undefined) : undefined}
          />
        );
       default:
        return (
          <Field
            name={getName()}
            render={({ input, meta }) => (
              <div>
                <FormLabel style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                  {intl.formatMessage({ id: label })}
                  {required && <span className={css.required}> *</span>}
                </FormLabel>
                <TextField
                  {...input}
                  id={getId()}
                  placeholder={placeholder ? intl.formatMessage({ id: placeholder }) : ''}
                  fullWidth
                  variant="outlined"
                  size="medium"
                  error={meta.touched && meta.error}
                  helperText={meta.touched && meta.error ? meta.error : ''}
                  className={css.textField}
                  InputProps={{
                    readOnly: readOnly,
                  }}
                />
              </div>
            )}
            validate={required ? (value) => (!value ? intl.formatMessage({ id: 'ApplicationForm.fieldRequired' }) : undefined) : undefined}
          />
        );
    }
  };

  return (
    <div 
      className={field.gridFullWidth ? classNames(css.questionField, css.gridFullWidth) : css.questionField}
      data-full-width={field.fullWidth || false}
      data-field-id={field.id}
    >
      {renderField()}
    </div>
  );
};

export default QuestionField;